/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ContentEditableEvent } from '@msdyn365-commerce/core';
import { LogLevel } from '@msdyn365-commerce/telemetry-internal';
import React, { ReactNode } from 'react';
import { Container, HeadingComponent, IHeadingComponentProps } from '../../components/base-components';
import { getColClassArray, showErrors } from '../../utils/classnames';
import { IDefaultContainer3SlotsData } from './default-container-3-slots.data';
import { IDefaultContainer3SlotsProps } from './default-container-3-slots.props.autogenerated';

interface IColSizes {
    left: string[];
    center: string[];
    right: string[];
}

/**
 *
 * CoreComponent component
 * @extends {React.PureComponent<IDefaultContainer3SlotsData<IDefaultContainer3SlotsData>>}
 */
class Default3SlotContainer extends React.PureComponent<IDefaultContainer3SlotsProps<IDefaultContainer3SlotsData>> {
    constructor(props: IDefaultContainer3SlotsProps<IDefaultContainer3SlotsData>) {
        super(props);
    }

    public render(): JSX.Element {
        const { slots, telemetry, context, config, data } = this.props;

        const slotsClassNames = this._getColClassName();
        const content = slots && slots.content;

        telemetry.log(LogLevel.Debug, "Default 3 Slot Container rendering for '{id}/{typeName}'", {
            values: [this.props.id, this.props.typeName]
        });
        if (content && content.length > 3) {
            telemetry.log(LogLevel.Warning, 'Default 3 Slot Container passed more than 3 slots');
        }
        const heading = config.heading || data.heading;
        const headingProps: IHeadingComponentProps = {
            ...heading,
            handleTextChange: this.handleTextChange,
            requestContext: this.props.context.request
        };
        return (
            <Container
                containerType={config.containerType}
                className={config.className}
                typeName={this.props.typeName}
                id={this.props.id}
                request={this.props.context.request}
                backgroundImage={config?.backgroundImage}
                backgroundImageRepeat={config?.backgroundImageRepeat}
                backgroundImageSize={config?.backgroundImageSize}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unnecessary-type-assertion
                moduleLayout={(config as any)?.msdyn365__moduleLayout}
                backgroundColor={this.props.config.backgroundColor}
            >
                {heading && <HeadingComponent className='container-heading' {...headingProps} />}
                {content && content[0] && <div className={slotsClassNames.left.join(' ')}>{content[0]}</div>}
                {content && content[1] && <div className={slotsClassNames.center.join(' ')}>{content[1]}</div>}
                {content && content[2] && <div className={slotsClassNames.right.join(' ')}>{content[2]}</div>}
                <>
                    {showErrors(context) &&
                        content &&
                        content.slice(2, content.length - 1).map((slot: ReactNode, index: number) => {
                            return (
                                <div className='col-12' key={index}>
                                    <div className='card'>
                                        <div className='card-header text-white bg-danger'>
                                            Slot exceeded maximum number of slots supported by container. (3)
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                </>
            </Container>
        );
    }

    public handleTextChange = (event: ContentEditableEvent) => (this.props.config.heading!.headingText = event.target.value);
    /**
     * Captures left and right column classnames from this.props.config. Returns col if no options specified.
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    public _getColClassName = (): IColSizes => {
        const { config } = this.props;

        const classNames = getColClassArray(config);

        if (!classNames.length) {
            return {
                left: ['col'],
                center: ['col'],
                right: ['col']
            };
        }

        return classNames.reduce(
            (memo: IColSizes, [left, center, right]: string[]) => {
                memo.left.push(left);
                memo.center.push(center);
                memo.right.push(right);
                return memo;
            },
            { left: [], center: [], right: [] }
        );
    };
}

export default Default3SlotContainer;
